import { MAX_HITS, MIN_QUERY_LENGTH, type BookSearch, type SearchHit } from "@msb/reader-bridge";
import { useEffect, useRef, useState } from "preact/hooks";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

interface Props {
  search: BookSearch;
  /** Dernière recherche, gardée quand le panneau est fermé puis rouvert. */
  initialQuery?: string;
  onQuery(query: string): void;
  /** `searching` : le livre n'a pas encore été parcouru en entier, la liste des occurrences n'est pas complète. */
  onSelect(hits: SearchHit[], index: number, searching: boolean): void;
}

const messages = defineMessages(
  {
    placeholder: "Rechercher dans le livre",
    clearLabel: "Effacer la recherche",
    clear: "Effacer",
    tooShort: "Saisissez au moins {min} caractères.",
    failed: "La recherche a échoué. Réessayez.",
    progress: "Recherche… {done} / {total}",
    results: ({ n, more }: { n: number; more: string }) => `${n}${more} résultat${n > 1 ? "s" : ""}`,
    firstResults: " (les {max} premiers)",
    noResults: "Aucun résultat.",
    resultsList: "Résultats de la recherche",
  },
  {
    placeholder: "Search the book",
    clearLabel: "Clear search",
    clear: "Clear",
    tooShort: "Type at least {min} characters.",
    failed: "The search failed. Please try again.",
    progress: "Searching… {done} / {total}",
    results: ({ n, more }) => `${n}${more} result${n > 1 ? "s" : ""}`,
    firstResults: " (the first {max})",
    noResults: "No results.",
    resultsList: "Search results",
  },
);

const DEBOUNCE_MS = 300;

/** « Rechercher » : tout le texte du livre, les occurrences avec leur contexte, un clic ouvre le passage. */
export function SearchPanel({ search, initialQuery = "", onQuery, onSelect }: Props) {
  const t = useT(messages);
  const [query, setQuery] = useState(initialQuery);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const trimmed = query.trim();
    setHits([]);
    setTruncated(false);
    setFailed(false);
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setProgress(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setProgress({ done: 0, total: 1 });
      search
        .run(trimmed, {
          signal: controller.signal,
          onHits: (found) => found.length && setHits((current) => [...current, ...found]),
          onProgress: (done, total) => setProgress({ done, total }),
        })
        .then((result) => {
          if (controller.signal.aborted) return;
          setTruncated(result.truncated);
          setProgress(null);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setFailed(true);
          setProgress(null);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, search]);

  const trimmed = query.trim();
  const status =
    trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH
      ? t("tooShort", { min: MIN_QUERY_LENGTH })
      : failed
        ? t("failed")
        : progress
          ? t("progress", { done: progress.done, total: progress.total })
          : trimmed.length >= MIN_QUERY_LENGTH
            ? hits.length
              ? `${t("results", { n: hits.length, more: truncated ? "+" : "" })}${truncated ? t("firstResults", { max: MAX_HITS }) : ""}`
              : t("noResults")
            : "";
  let lastSection: string | undefined;
  return (
    <div class="msb-search">
      <label class="msb-search__field">
        <Icon name="search" size={20} />
        <input
          ref={input}
          type="search"
          value={query}
          placeholder={t("placeholder")}
          aria-label={t("placeholder")}
          autoComplete="off"
          enterKeyHint="search"
          onInput={(event) => {
            const value = event.currentTarget.value;
            setQuery(value);
            onQuery(value);
          }}
        />
        {query && (
          <button
            type="button"
            class="msb-search__clear"
            aria-label={t("clearLabel")}
            title={t("clear")}
            onClick={() => {
              setQuery("");
              onQuery("");
              input.current?.focus();
            }}
          >
            <Icon name="close" size={22} />
          </button>
        )}
      </label>
      <p class="msb-search__status" role="status">
        {status}
      </p>
      {hits.length > 0 && (
        <ol class="msb-list msb-search__results" aria-label={t("resultsList")}>
          {hits.map((hit, index) => {
            const heading = hit.section !== lastSection ? hit.section : undefined;
            lastSection = hit.section;
            return (
              <li key={`${hit.locator.href}-${index}`}>
                {heading && <h3 class="msb-search__section">{heading}</h3>}
                <button type="button" class="msb-list__item msb-search__hit" onClick={() => onSelect(hits, index, progress !== null)}>
                  <span>
                    <span>{hit.before.replace(/^\S*\s/, hit.before.length >= 40 ? "… " : "$&")}</span>
                    <mark>{hit.match}</mark>
                    <span>{hit.after}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
